import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    FiArrowLeft,
    FiCheckCircle,
    FiClock,
    FiFileText,
    FiLoader,
    FiPrinter,
    FiRefreshCw,
    FiSlash,
} from "react-icons/fi";

import {
    useDispatch,
    useSelector,
} from "react-redux";

import {
    useHistory,
    useParams,
} from "react-router-dom";

import {
    cancelCreditNote,
    confirmCreditNote,
    fetchCreditNoteById,
    selectedCreditNoteSelector,
    creditNoteLoadingSelector,
    creditNoteErrorSelector,
} from "../../../../../../store/billing/creditNotes/creditNoteSlice";

import CreditNoteItemsTable from "./components/CreditNoteItemsTable";
import CreditNoteSummary from "./components/CreditNoteSummary";

import "./CreditNoteViewPage.css";

/*
|--------------------------------------------------------------------------
| ROUTES
|--------------------------------------------------------------------------
*/

const CREDIT_NOTE_ROUTE =
    "/billing/credit-notes";

const SALE_INVOICE_ROUTE =
    "/billing/sale-invoices";

/*
|--------------------------------------------------------------------------
| HELPERS
|--------------------------------------------------------------------------
*/

const toNumber = (
    value,
    fallback = 0
) => {
    const number =
        Number(value);

    return Number.isFinite(
        number
    )
        ? number
        : fallback;
};

const roundMoney = (
    value
) =>
    Math.round(
        (
            toNumber(
                value
            ) +
            Number.EPSILON
        ) *
            100
    ) / 100;

const formatCurrency = (
    value
) =>
    new Intl.NumberFormat(
        "en-IN",
        {
            style:
                "currency",

            currency:
                "INR",

            minimumFractionDigits:
                2,

            maximumFractionDigits:
                2,
        }
    ).format(
        roundMoney(
            value
        )
    );

const formatDate = (
    value,
    includeTime = false
) => {
    if (!value) {
        return "-";
    }

    const date =
        new Date(value);

    if (
        Number.isNaN(
            date.getTime()
        )
    ) {
        return "-";
    }

    return new Intl.DateTimeFormat(
        "en-IN",
        includeTime
            ? {
                  day:
                      "2-digit",

                  month:
                      "short",

                  year:
                      "numeric",

                  hour:
                      "2-digit",

                  minute:
                      "2-digit",
              }
            : {
                  day:
                      "2-digit",

                  month:
                      "short",

                  year:
                      "numeric",
              }
    ).format(
        date
    );
};

const formatStatus = (
    value
) =>
    String(
        value ||
            "-"
    )
        .replace(
            /_/g,
            " "
        )
        .toLowerCase()
        .replace(
            /\b\w/g,
            (
                character
            ) =>
                character.toUpperCase()
        );

const getErrorMessage = (
    error,
    fallback
) =>
    error?.message ||
    error?.payload
        ?.message ||
    error?.payload ||
    error?.response
        ?.data
        ?.message ||
    fallback;

const getCreditAmount = (
    creditNote
) =>
    roundMoney(
        creditNote
            ?.totals
            ?.creditAmount ??
        creditNote
            ?.creditAmount ??
        0
    );

const getCustomerName = (
    creditNote
) =>
    creditNote
        ?.customerSnapshot
        ?.customerName ||
    creditNote
        ?.customerSnapshot
        ?.name ||
    creditNote
        ?.customer
        ?.customerName ||
    creditNote
        ?.customer
        ?.name ||
    "Walk-in Customer";

const getCustomerMobile = (
    creditNote
) =>
    creditNote
        ?.customerSnapshot
        ?.mobile ||
    creditNote
        ?.customer
        ?.mobile ||
    "-";

const getCustomerEmail = (
    creditNote
) =>
    creditNote
        ?.customerSnapshot
        ?.email ||
    creditNote
        ?.customer
        ?.email ||
    "-";

const getInvoiceId = (
    creditNote
) => {
    const saleInvoice =
        creditNote
            ?.saleInvoice;

    if (
        saleInvoice &&
        typeof saleInvoice ===
            "object"
    ) {
        return (
            saleInvoice?._id ||
            saleInvoice?.id ||
            ""
        );
    }

    return (
        saleInvoice ||
        creditNote
            ?.saleInvoiceId ||
        ""
    );
};

/*
|--------------------------------------------------------------------------
| COMPONENT
|--------------------------------------------------------------------------
*/

const CreditNoteViewPage = () => {
    const dispatch =
        useDispatch();

    const history =
        useHistory();

    const {
        id,
    } =
        useParams();

    const creditNote =
        useSelector(
            selectedCreditNoteSelector
        );

    const loading =
        useSelector(
            creditNoteLoadingSelector
        ) || {};

    const errors =
        useSelector(
            creditNoteErrorSelector
        ) || {};

    const [
        localError,
        setLocalError,
    ] = useState("");

    const [
        actionLoading,
        setActionLoading,
    ] = useState("");

    /*
    |--------------------------------------------------------------------------
    | VALUES
    |--------------------------------------------------------------------------
    */

    const status =
        String(
            creditNote
                ?.status ||
                "DRAFT"
        ).toUpperCase();

    const items =
        useMemo(
            () =>
                Array.isArray(
                    creditNote
                        ?.items
                )
                    ? creditNote.items
                    : [],
            [
                creditNote,
            ]
        );

    const invoiceId =
        getInvoiceId(
            creditNote
        );

    const creditAmount =
        getCreditAmount(
            creditNote
        );

    /*
    |--------------------------------------------------------------------------
    | LOAD
    |--------------------------------------------------------------------------
    */

    const loadCreditNote =
        useCallback(
            async () => {
                if (!id) {
                    return;
                }

                try {
                    setLocalError(
                        ""
                    );

                    await dispatch(
                        fetchCreditNoteById(
                            id
                        )
                    ).unwrap();
                } catch (
                    error
                ) {
                    setLocalError(
                        getErrorMessage(
                            error,
                            "Unable to load credit note."
                        )
                    );
                }
            },
            [
                dispatch,
                id,
            ]
        );

    useEffect(
        () => {
            loadCreditNote();
        },
        [
            loadCreditNote,
        ]
    );

    /*
    |--------------------------------------------------------------------------
    | BACK
    |--------------------------------------------------------------------------
    */

    const handleBack =
        () => {
            history.push(
                CREDIT_NOTE_ROUTE
            );
        };

    /*
    |--------------------------------------------------------------------------
    | VIEW INVOICE
    |--------------------------------------------------------------------------
    */

    const handleViewInvoice =
        () => {
            if (
                !invoiceId
            ) {
                setLocalError(
                    "Sale invoice ID is missing."
                );

                return;
            }

            history.push(
                `${SALE_INVOICE_ROUTE}/${invoiceId}`
            );
        };

    /*
    |--------------------------------------------------------------------------
    | CONFIRM
    |--------------------------------------------------------------------------
    */

    const handleConfirm =
        async () => {
            if (
                !id ||
                status !==
                    "DRAFT"
            ) {
                return;
            }

            const approved =
                window.confirm(
                    `Confirm credit note ${
                        creditNote
                            ?.creditNoteNumber ||
                        ""
                    }?\n\nStock and customer ledger will be updated.`
                );

            if (
                !approved
            ) {
                return;
            }

            try {
                setLocalError(
                    ""
                );

                setActionLoading(
                    "CONFIRM"
                );

                await dispatch(
                    confirmCreditNote(
                        id
                    )
                ).unwrap();

                await loadCreditNote();
            } catch (
                error
            ) {
                setLocalError(
                    getErrorMessage(
                        error,
                        "Unable to confirm credit note."
                    )
                );
            } finally {
                setActionLoading(
                    ""
                );
            }
        };

    /*
    |--------------------------------------------------------------------------
    | CANCEL
    |--------------------------------------------------------------------------
    */

    const handleCancel =
        async () => {
            if (
                !id ||
                status !==
                    "DRAFT"
            ) {
                return;
            }

            const reason =
                window.prompt(
                    `Enter cancellation reason for ${
                        creditNote
                            ?.creditNoteNumber ||
                        "this credit note"
                    }:`
                );

            const normalizedReason =
                String(
                    reason ||
                        ""
                ).trim();

            if (
                normalizedReason.length <
                3
            ) {
                return;
            }

            try {
                setLocalError(
                    ""
                );

                setActionLoading(
                    "CANCEL"
                );

                await dispatch(
                    cancelCreditNote({
                        creditNoteId:
                            id,

                        cancellationReason:
                            normalizedReason,
                    })
                ).unwrap();

                await loadCreditNote();
            } catch (
                error
            ) {
                setLocalError(
                    getErrorMessage(
                        error,
                        "Unable to cancel credit note."
                    )
                );
            } finally {
                setActionLoading(
                    ""
                );
            }
        };

    /*
    |--------------------------------------------------------------------------
    | PRINT
    |--------------------------------------------------------------------------
    */

    const handlePrint =
        () => {
            window.print();
        };

    /*
    |--------------------------------------------------------------------------
    | LOADING
    |--------------------------------------------------------------------------
    */

    if (
        loading.details &&
        !creditNote
    ) {
        return (
            <div className="credit-note-view-page">
                <div className="credit-note-view-loading">
                    <FiLoader className="credit-note-view-spinner" />

                    <strong>
                        Loading credit note...
                    </strong>
                </div>
            </div>
        );
    }

    /*
    |--------------------------------------------------------------------------
    | RENDER
    |--------------------------------------------------------------------------
    */

    return (
        <div className="credit-note-view-page">
            {/* HEADER */}

            <header className="credit-note-view-header">
                <div className="credit-note-view-header-left">
                    <button
                        type="button"
                        className="credit-note-view-back-button"
                        onClick={
                            handleBack
                        }
                    >
                        <FiArrowLeft />
                    </button>

                    <span className="credit-note-view-header-icon">
                        <FiFileText />
                    </span>

                    <div>
                        <small>
                            CREDIT NOTE
                        </small>

                        <h1>
                            {creditNote
                                ?.creditNoteNumber ||
                                "Credit Note"}
                        </h1>

                        <p>
                            Against{" "}
                            <button
                                type="button"
                                onClick={
                                    handleViewInvoice
                                }
                            >
                                {creditNote
                                    ?.saleInvoiceNumber ||
                                    "-"}
                            </button>
                        </p>
                    </div>
                </div>

                <div className="credit-note-view-header-actions">
                    <button
                        type="button"
                        disabled={
                            loading.details
                        }
                        onClick={
                            loadCreditNote
                        }
                    >
                        <FiRefreshCw
                            className={
                                loading.details
                                    ? "credit-note-view-spinner"
                                    : ""
                            }
                        />

                        Refresh
                    </button>

                    <button
                        type="button"
                        onClick={
                            handlePrint
                        }
                    >
                        <FiPrinter />

                        Print
                    </button>

                    {status ===
                        "DRAFT" && (
                        <>
                            <button
                                type="button"
                                className="credit-note-view-confirm-button"
                                disabled={
                                    Boolean(
                                        actionLoading
                                    )
                                }
                                onClick={
                                    handleConfirm
                                }
                            >
                                {actionLoading ===
                                "CONFIRM" ? (
                                    <FiLoader className="credit-note-view-spinner" />
                                ) : (
                                    <FiCheckCircle />
                                )}

                                Confirm
                            </button>

                            <button
                                type="button"
                                className="credit-note-view-cancel-button"
                                disabled={
                                    Boolean(
                                        actionLoading
                                    )
                                }
                                onClick={
                                    handleCancel
                                }
                            >
                                {actionLoading ===
                                "CANCEL" ? (
                                    <FiLoader className="credit-note-view-spinner" />
                                ) : (
                                    <FiSlash />
                                )}

                                Cancel
                            </button>
                        </>
                    )}
                </div>
            </header>

            {/* ERROR */}

            {(localError ||
                errors.details ||
                errors.confirm ||
                errors.cancel) && (
                <div className="credit-note-view-alert">
                    {localError ||
                        errors.details ||
                        errors.confirm ||
                        errors.cancel}
                </div>
            )}

            {/* TOP SUMMARY */}

            <section className="credit-note-view-top-grid">
                <div className="credit-note-view-info-card">
                    <div className="credit-note-view-status-row">
                        <span
                            className={`credit-note-view-status credit-note-view-status-${status.toLowerCase()}`}
                        >
                            {status ===
                            "CONFIRMED" ? (
                                <FiCheckCircle />
                            ) : status ===
                              "CANCELLED" ? (
                                <FiSlash />
                            ) : (
                                <FiClock />
                            )}

                            {formatStatus(
                                status
                            )}
                        </span>
                    </div>

                    <div className="credit-note-view-customer">
                        <span>
                            Customer
                        </span>

                        <strong>
                            {getCustomerName(
                                creditNote
                            )}
                        </strong>

                        <small>
                            {getCustomerMobile(
                                creditNote
                            )}
                        </small>

                        <small>
                            {getCustomerEmail(
                                creditNote
                            )}
                        </small>
                    </div>
                </div>

                <div className="credit-note-view-amount-card">
                    <span>
                        Credit Amount
                    </span>

                    <strong>
                        {formatCurrency(
                            creditAmount
                        )}
                    </strong>

                    <small>
                        {formatDate(
                            creditNote
                                ?.creditNoteDate
                        )}
                    </small>
                </div>
            </section>

            {/* INFORMATION */}

            <section className="credit-note-view-card">
                <header className="credit-note-view-section-header">
                    <div>
                        <h2>
                            Credit Note Information
                        </h2>

                        <p>
                            Basic document and return details.
                        </p>
                    </div>
                </header>

                <div className="credit-note-view-info-grid">
                    <div>
                        <span>
                            Credit Note Number
                        </span>

                        <strong>
                            {creditNote
                                ?.creditNoteNumber ||
                                "-"}
                        </strong>
                    </div>

                    <div>
                        <span>
                            Credit Note Date
                        </span>

                        <strong>
                            {formatDate(
                                creditNote
                                    ?.creditNoteDate
                            )}
                        </strong>
                    </div>

                    <div>
                        <span>
                            Source Invoice
                        </span>

                        <button
                            type="button"
                            onClick={
                                handleViewInvoice
                            }
                        >
                            {creditNote
                                ?.saleInvoiceNumber ||
                                "-"}
                        </button>
                    </div>

                    <div>
                        <span>
                            Reason
                        </span>

                        <strong>
                            {formatStatus(
                                creditNote
                                    ?.reason
                            )}
                        </strong>
                    </div>

                    <div>
                        <span>
                            Financial Year
                        </span>

                        <strong>
                            {creditNote
                                ?.financialYear ||
                                "-"}
                        </strong>
                    </div>

                    <div>
                        <span>
                            Status
                        </span>

                        <strong>
                            {formatStatus(
                                status
                            )}
                        </strong>
                    </div>
                </div>

                {creditNote
                    ?.reasonDetails && (
                    <div className="credit-note-view-reason">
                        <span>
                            Reason Details
                        </span>

                        <p>
                            {
                                creditNote
                                    .reasonDetails
                            }
                        </p>
                    </div>
                )}
            </section>

            {/* EXISTING ITEMS COMPONENT */}

            <section className="credit-note-view-card">
                <header className="credit-note-view-section-header">
                    <div>
                        <h2>
                            Returned Items
                        </h2>

                        <p>
                            Items and quantities credited against the invoice.
                        </p>
                    </div>

                    <span className="credit-note-view-count">
                        {items.length}{" "}
                        {items.length ===
                        1
                            ? "item"
                            : "items"}
                    </span>
                </header>

                <CreditNoteItemsTable
                    items={
                        items
                    }
                />
            </section>

            {/* BOTTOM */}

            <div className="credit-note-view-bottom-grid">
                <section className="credit-note-view-card">
                    <h2>
                        Notes
                    </h2>

                    <p className="credit-note-view-notes">
                        {creditNote
                            ?.notes ||
                            "No notes added."}
                    </p>

                    {status ===
                        "CANCELLED" && (
                        <div className="credit-note-view-cancellation">
                            <span>
                                Cancellation Reason
                            </span>

                            <strong>
                                {creditNote
                                    ?.cancellationReason ||
                                    "-"}
                            </strong>

                            <small>
                                Cancelled{" "}
                                {formatDate(
                                    creditNote
                                        ?.cancelledAt,
                                    true
                                )}
                            </small>
                        </div>
                    )}
                </section>

                <CreditNoteSummary
                    creditNote={
                        creditNote
                    }
                />
            </div>
        </div>
    );
};

export default CreditNoteViewPage;